import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import MobileScanInput from '../components/MobileScanInput';
import {
    ArrowLeft,
    ScanLine,
    History,
    RotateCcw,
    Package,
    MapPin,
    Box,
    Truck
} from 'lucide-react';
import { useMobile } from '../context/MobileContext';
import { WarehouseBottomNav } from '../components/WarehouseUIKit';
import { useWarehouseTheme } from '../context/WarehouseThemeContext';
import '../styles/warehouse.css';

const MobileScan = () => {
    const navigate = useNavigate();
    const { scanHistory, addScan } = useMobile();
    const { isDark } = useWarehouseTheme();
    const [lastResult, setLastResult] = useState(null);

    const handleScanSuccess = (barcode, result) => {
        setLastResult(result);
    };

    const getIconForContext = (context) => {
        switch (context) {
            case 'RECEIVING': return Package;
            case 'PICKING': return ScanLine;
            case 'PACKING': return Box;
            case 'LOADOUT': return Truck;
            default: return ScanLine;
        }
    };

    return (
        <div className="warehouse-app min-h-screen pb-20">
            <header className="wh-safe-top px-4 py-4 wh-bg-primary text-white flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10"
                    onClick={() => navigate('/mobile')}
                >
                    <ArrowLeft className="h-6 w-6" />
                </Button>
                <h1 className="text-xl font-bold">General Scan</h1>
            </header>

            <div className="p-4 space-y-6">
                {/* Main Scanner Section */}
                <MobileScanInput
                    title="Universal Scanner"
                    subtitle="Scan any barcode to identify or validate"
                    gateId="GATE_GENERAL_SCAN"
                    barcodeType="ANY"
                    scanContext="GENERAL"
                    onScanSuccess={handleScanSuccess}
                />

                {/* Scan Results / History */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="wh-text-subtitle flex items-center gap-2">
                            <History className="h-4 w-4" />
                            Recent Scans
                        </h2>
                        <Button variant="ghost" size="sm" className="text-sm h-8" onClick={() => navigate('/mobile')}>
                            Close
                        </Button>
                    </div>

                    <Card elevated>
                        <CardContent className="p-0 divide-y">
                            {scanHistory.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground">
                                    <ScanLine className="h-12 w-12 mx-auto opacity-20 mb-2" />
                                    <p>No scans yet today</p>
                                </div>
                            ) : (
                                scanHistory.slice(0, 10).map((scan, idx) => {
                                    const Icon = getIconForContext(scan.context);
                                    return (
                                        <div key={idx} className="p-4 flex items-center gap-4">
                                            <div className={`p-2 rounded-lg ${scan.result === 'OK' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                                                }`}>
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-mono font-bold truncate">{scan.barcode}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                                                        {scan.context}
                                                    </Badge>
                                                    <span className="text-[10px] text-muted-foreground">
                                                        {new Date(scan.timestamp).toLocaleTimeString()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className={`text-xs font-bold ${scan.result === 'OK' ? 'text-green-500' : 'text-red-500'
                                                    }`}>
                                                    {scan.result}
                                                </span>
                                                {scan.message && (
                                                    <p className="text-[10px] text-muted-foreground truncate max-w-[80px]">
                                                        {scan.message}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default MobileScan;
